import { ErrorMessage, useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { expenseActions } from "../../sagas/expenseSlice";
import * as Yup from 'yup';
import { Badge, Button, Card, CardBody, Col, Container, Form, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Row, Spinner, Table } from "reactstrap";
import { FaCalendar, FaCreditCard, FaCubes, FaDollarSign, FaEdit, FaFilter, FaPlus, FaSearch, FaTrash, FaUserFriends } from "react-icons/fa";
import Swal from "sweetalert2";
import { formatPrice } from "../../helpers/utility";
import Select from "react-select";
import { debounce } from "lodash";
import { commercialActions } from "../../sagas/commercialSlice";
import { commissionPlanActions } from "../../sagas/commissionPlanSlice";

const PERIOD_OPTIONS = [
  { value: 'DAYS_15', label: '15 jours' },
  { value: 'DAYS_30', label: '30 jours' },
  { value: 'DAYS_45', label: '45 jours' },
  { value: 'DAYS_60', label: '60 jours' },
  { value: 'DAYS_90', label: '90 jours' },
];

const PERIOD_LABELS = {
  DAYS_15: "15 jours",
  DAYS_30: "30 jours",
  DAYS_45: "45 jours",
  DAYS_60: "60 jours",
  DAYS_90: "90 jours",
};

const CommercialList = () => {
  const dispatch = useDispatch();
  const { list, loading, performanceById, perfLoading, perfPeriod, perfRefDate } = useSelector((state) => state.commercials); // Adjust state path as needed
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    query: ''
  });
  const { list: planList, loading: plansLoading } = useSelector((s) => s.commissionPlans || { list: [], loading: false });
  const { ledgerByCommercialId, ledgerLoading } = useSelector(s => s.commercials);

  const [perfParams, setPerfParams] = useState({
    period: "DAYS_30",
    refDate: new Date().toISOString().slice(0, 10)
  });

  const [commissionModal, setCommissionModal] = useState({
    visible: false,
    loading: false,
    commercial: null,
    period: PERIOD_OPTIONS[1].value, // défaut 30j
    refDate: new Date().toISOString().slice(0, 10), // yyyy-mm-dd
    preview: null, // payload retourné par /preview
  });

  const [ledgerModal, setLedgerModal] = useState({
    visible: false,
    commercial: null,
  });

  const openLedger = (commercial) => {
    setLedgerModal({ visible: true, commercial });
    dispatch(commercialActions.listLedger({ commercialId: commercial.id }));
  };
  const closeLedger = () => setLedgerModal({ visible: false, commercial: null });

  const [planModal, setPlanModal] = useState({
    visible: false,
    data: {
      period: "DAYS_30",
      baseType: "ON_REVENUE",
      objectiveAmount: 0,
      baseRate: 0.05,
      bonusRate: 0.02,
      progressiveTiersEnabled: false,
      tiers: [] // quick create sans paliers, tu peux en ajouter plus tard
    }
  });

  const openCommissionModal = (commercial) => {
    console.log(commercial)
    // Try to read the period from the commercial’s assigned plan
    const planPeriod = planList.find(p => p.id === commercial.commissionPlanId)?.period || null;

    setCommissionModal(prev => ({
      ...prev,
      visible: true,
      commercial,
      // lock the period to the plan’s period (can be null if no plan yet)
      period: planPeriod,
      preview: null
    }));
  };

  const closeCommissionModal = () => {
    setCommissionModal({
      visible: false,
      loading: false,
      commercial: null,
      period: PERIOD_OPTIONS[1].value,
      refDate: new Date().toISOString().slice(0, 10),
      preview: null,
    });
  };

  // Prévisualiser via Saga -> appelle GET /api/commissions/period/preview
  const handlePreviewCommission = () => {
    const { commercial, period, refDate } = commissionModal;
    if (!commercial) return;
    setCommissionModal(p => ({ ...p, loading: true }));
    dispatch(commercialActions.previewCommission({
      commercialId: commercial.id,
      period,
      refDate, // yyyy-mm-dd
      onSuccess: (data) => {
        setCommissionModal(p => ({ ...p, loading: false, preview: data }));
      },
      onError: (msg) => {
        setCommissionModal(p => ({ ...p, loading: false }));
        Swal.fire({ icon: 'error', title: 'Erreur', text: msg || 'Prévisualisation impossible' });
      }
    }));
  };

  const handlePostCommission = () => {
    const { commercial, period, refDate } = commissionModal;
    if (!commercial) return;
    setCommissionModal(p => ({ ...p, loading: true }));
    dispatch(commercialActions.postCommission({
      commercialId: commercial.id,
      period,
      refDate,
      ref: 'ui-manual', // trace
      onSuccess: () => {
        setCommissionModal(p => ({ ...p, loading: false }));
        Swal.fire({ icon: 'success', title: 'Commission validée', text: 'La commission de la période a été enregistrée.' });
        closeCommissionModal();
      },
      onError: (msg) => {
        setCommissionModal(p => ({ ...p, loading: false }));
        Swal.fire({ icon: 'error', title: 'Erreur', text: msg || 'Validation impossible' });
      }
    }));
  };

  const searchInputRef = useRef(null);

  useEffect(() => {
    dispatch(commercialActions.list({ ...filters })); // Fetch categories on component mount
    dispatch(commissionPlanActions.list({})); // <— charger les plans
    dispatch(commercialActions.loadPerformance({ period: perfParams.period, refDate: perfParams.refDate }));

  }, []);

  useEffect(() => {
    dispatch(commercialActions.loadPerformance({ period: perfParams.period, refDate: perfParams.refDate }));
  }, [perfParams.period, perfParams.refDate]);

  useEffect(() => {
    // Auto focus on mount
    searchInputRef.current?.focus();

    // Add keyboard shortcut
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [list]);

  const clearFilters = () => {
    setFilters({
      query: '',
    });
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      dispatch(commercialActions.list({
        query: query
      }));
    }, 1500), // Wait 1.5 seconds after user stops typing
    [filters.query]
  );

  const handleCloseModal = () => {
    setIsModalVisible(false);
    formik.resetForm();
    dispatch(commercialActions.list({ ...filters })); // Fetch categories on component mount
  };

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };


  const formik = useFormik({
    initialValues: {
      id: null,
      fullName: '',
      email: '',
      phone: '',
      contractType: '',
      hireDate: null,
      commissionPlanId: null,
      notes: '',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      let payload = {
        data: {
          id: values.id,
          fullName: values.fullName,
          email: values.email,
          phone: values.phone,
          contractType: values.contractType,
          hireDate: values.hireDate,
          commissionPlanId: values.commissionPlanId,
          notes: values.notes,
        },
        onSuccess: () => {
          Swal.fire({
            icon: 'success',
            title: 'Succès',
            text: 'Commercial enregistrés avec succès!',
            confirmButtonText: 'OK',
          });
          handleCloseModal();
        }
      }
      if (values.id) {
        dispatch(commercialActions.update(payload));
      } else {
        dispatch(commercialActions.create(payload));
      }
    },
  });


  const handleAddCommercial = () => {
    formik.handleSubmit();
  }

  const handleDeleteCommercial = (id) => {
    Swal.fire({
      title: t('commercial.confirmDeleteTitle'),
      text: t('commercial.confirmDeleteText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common.yes'),
      cancelButtonText: t('common.no')
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(commercialActions.delete(
          { id }
        ));
      }
    });
  };

  const handleEditCommercial = (id) => {
    const commercial = list.find(com => com.id === id);
    if (commercial) {
      formik.setValues(commercial);
      toggleModal();
    }
  };

  return (
    <Container fluid>
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <div className="mt-2 text-muted">{t('common.loading')}</div>
        </div>
      ) : (
        <>
          <Row className="mb-4" >
            <Col>
              <Card className="shadow-sm">
                <CardBody className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 d-flex align-items-center">
                    <FaCubes className="text-primary me-2" />
                    {t('commercial.list')}
                  </h4>
                  <Button
                    color="primary"
                    onClick={() => toggleModal()} // Go back to the previous page
                    className="d-flex align-items-center"
                  >
                    <FaPlus className="me-2" />
                    {t('commercial.new')}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Card className="shadow-sm">
            <CardBody>
              <div className="filter-container mb-4">
                <Row className="mb-3 g-2 align-items-end">
                  <Col lg={4} md={4} sm={4} xs={12}>
                    <FormGroup className="mb-0">
                      <Label className="mb-1">{t('reglement.search')}</Label>
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <FaSearch />
                        </span>
                        <Input
                          type="text"
                          innerRef={searchInputRef}
                          placeholder={t('commercial.searchPlaceholder')}
                          value={filters.query}
                          onChange={e => {
                            const value = e.target.value.toUpperCase();
                            setFilters(prevFilters => ({
                              ...prevFilters,
                              query: value
                            }));
                            debouncedSearch(value);
                          }}
                          className="text-uppercase"
                          style={{ textTransform: 'uppercase' }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              debouncedSearch(e.target.value.toUpperCase());
                            }
                          }}
                        />
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label className="mb-1">Période commission</Label>
                      <Input
                        type="select"
                        value={perfParams.period}
                        onChange={(e) => setPerfParams(p => ({ ...p, period: e.target.value }))}
                      >
                        <option value="DAYS_15">15 jours</option>
                        <option value="DAYS_30">30 jours</option>
                        <option value="DAYS_45">45 jours</option>
                        <option value="DAYS_60">60 jours</option>
                        <option value="DAYS_90">90 jours</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label className="mb-1">Date de référence</Label>
                      <Input
                        type="date"
                        value={perfParams.refDate}
                        onChange={(e) => setPerfParams(p => ({ ...p, refDate: e.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    {perfLoading && <Spinner size="sm" className="ms-2" />}
                  </Col>
                </Row>
              </div>
              <Table bordered>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Nom complet</th>
                    <th>Téléphone</th>
                    <th>Type contrat</th>
                    <th>Plan</th>               {/** new */}
                    <th>Objectif</th>           {/** new */}
                    <th>Réalisé (base)</th>     {/** new */}
                    <th>Progression</th>        {/** new */}
                    <th>Est. Commission</th>    {/** new */}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length > 0 && list.map((ex) => {
                    const perf = performanceById[ex.id]; // { plan, baseUsed, baseTotal, objective, totalCommission, ... }
                    console.log(perf)
                    const objective = Number(perf?.objective || 0);
                    const baseTotal = Number(perf?.baseTotal || 0);
                    const pct = objective > 0 ? Math.min(100, Math.round((baseTotal / objective) * 100)) : 0;

                    const planBadge = perf?.plan
                      ? `${perf.plan.period} • ${perf.plan.baseType === "ON_REVENUE" ? "CA" : "Marge"}`
                      : "-";


                    return (
                      <tr key={ex.id}>
                        <td>{ex.code}</td>
                        <td>{ex.fullName}</td>
                        <td>{ex.phone}</td>
                        <td>{ex.contractType}</td>
                        {/* Plan */}
                        <td>
                          {perf?.plan ? (
                            <span className="badge bg-secondary">{planBadge}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>

                        {/* Objectif */}
                        <td>{formatPrice(objective)}</td>

                        {/* Réalisé */}
                        <td>{formatPrice(baseTotal)}</td>

                        {/* Progression */}
                        <td style={{ minWidth: 180 }}>
                          <div className="d-flex align-items-center gap-2">
                            <div className="flex-grow-1">
                              <Progress value={pct}>{pct}%</Progress>
                            </div>
                          </div>
                        </td>

                        {/* Est. Commission (preview total) */}
                        <td><strong>{formatPrice(perf?.totalCommission || 0)}</strong></td>

                        <td>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleDeleteCommercial(ex.id)}
                          >
                            <FaTrash />
                          </Button>
                          <Button
                            className="ms-1"
                            color="warning"
                            size="sm"
                            onClick={() => handleEditCommercial(ex.id)}
                          >
                            <FaEdit />
                          </Button>
                          {ex.commissionPlanId && (
                            <Button
                              className="ms-1"
                              color="info"
                              size="sm"
                              onClick={() => openCommissionModal(ex)}
                              title="Commission période"
                            >
                              %
                            </Button>
                          )}
                          <Button
                            className="ms-1"
                            color="secondary"
                            size="sm"
                            onClick={() => openLedger(ex)}
                            title="Historique commissions"
                          >
                            Hist.
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
              {list.length === 0 && (
                <div className="text-center mt-4">
                  <FaUserFriends size={50} className="text-muted" />
                  <h5 className="mt-2">{t('commercial.noCommercials')}</h5>
                </div>
              )}
            </CardBody>
          </Card>
        </>)}
      <Modal isOpen={isModalVisible} toggle={handleCloseModal} size="xl">
        <ModalHeader toggle={handleCloseModal}>
          {formik.values.id ? `${t('commercial.edit')} ${formik.values.code}` : t('commercial.new')}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="fullName">{t('commercial.fullName')}</Label>
                  <Input
                    type="text"
                    name="fullName"
                    id="fullName"
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    required
                    maxLength={120}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="email">{t('commercial.email')}</Label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    maxLength={120}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="phone">{t('commercial.phone')}</Label>
                  <Input
                    type="text"
                    name="phone"
                    id="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    maxLength={30}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>

              <Col md={4}>
                <FormGroup>
                  <Label for="contractType">{t('commercial.contractType')}</Label>
                  <Input
                    type="select"
                    name="contractType"
                    id="contractType"
                    value={formik.values.contractType}
                    onChange={formik.handleChange}
                    required
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="CDD">{t('commercial.cdd')}</option>
                    <option value="CDI">{t('commercial.cdi')}</option>
                    <option value="FREELANCE">{t('commercial.freelance')}</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="hireDate">{t('commercial.hireDate')}</Label>
                  <Input
                    type="date"
                    name="hireDate"
                    id="hireDate"
                    value={formik.values.hireDate}
                    onChange={formik.handleChange}
                    required
                  />
                </FormGroup>
              </Col>

            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="commissionPlanId">Plan de commission</Label>
                  <div className="d-flex gap-2">
                    <Input
                      type="select"
                      name="commissionPlanId"
                      id="commissionPlanId"
                      value={formik.values.commissionPlanId || ""}
                      onChange={formik.handleChange}
                      disabled={plansLoading}
                      required
                    >
                      <option value="">{plansLoading ? 'Chargement...' : '— Sélectionner —'}</option>
                      {planList.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.period} • {p.baseType} • Obj: {Number(p.objectiveAmount).toLocaleString()}
                        </option>
                      ))}
                    </Input>

                    <Button
                      color="success"
                      size="sm"
                      type="button"
                      onClick={() => setPlanModal((m) => ({ ...m, visible: true }))}
                    >
                      Créer
                    </Button>
                  </div>
                  <small className="text-muted">
                    Sélectionne un plan existant ou crée un plan si la liste est vide.
                  </small>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="notes">{t('commercial.notes')}</Label>
                  <Input
                    type="textarea"
                    name="notes"
                    id="notes"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    maxLength={500}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddCommercial}>
            {t('common.save')}
          </Button>{' '}
          <Button color="secondary" onClick={handleCloseModal}>
            {t('common.cancel')}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={commissionModal.visible} toggle={closeCommissionModal} size="lg">
        <ModalHeader toggle={closeCommissionModal}>
          {t('commercial.commissionPeriod')} — {commissionModal.commercial?.fullName}
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md={4}>
              <FormGroup>
                <Label>Période (plan)</Label>
                <div>
                  {commissionModal.period ? (
                    <span className="badge bg-secondary">
                      {PERIOD_LABELS[commissionModal.period] || commissionModal.period}
                    </span>
                  ) : (
                    <span className="text-danger small">
                      Aucun plan assigné à ce commercial — veuillez assigner/créer un plan.
                    </span>
                  )}
                </div>
                <small className="text-muted">
                  La période provient du plan de commission du commercial.
                </small>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Date de référence</Label>
                <Input
                  type="date"
                  value={commissionModal.refDate}
                  onChange={(e) => setCommissionModal(p => ({ ...p, refDate: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <div className="d-flex gap-2">
                <Button color="secondary" onClick={handlePreviewCommission} disabled={commissionModal.loading}>
                  {commissionModal.loading ? <Spinner size="sm" /> : 'Prévisualiser'}
                </Button>
                <Button color="primary" onClick={handlePostCommission} disabled={commissionModal.loading || !commissionModal.preview}>
                  Valider
                </Button>
              </div>
            </Col>
          </Row>

          {/* Résultat */}
          {commissionModal.preview && (
            <Card className="mt-3">
              <CardBody>
                <Row>
                  <Col md={6}>
                    <h6 className="text-muted mb-2">Fenêtre</h6>
                    <div><strong>Type:</strong> {commissionModal.preview.periodType}</div>
                    <div><strong>Du:</strong> {commissionModal.preview.periodStart}</div>
                    <div><strong>Au:</strong> {commissionModal.preview.periodEnd}</div>
                  </Col>
                  <Col md={6}>
                    <h6 className="text-muted mb-2">Base</h6>
                    <div><strong>Base utilisée:</strong> {commissionModal.preview.baseUsed}</div>
                    <div><strong>Total base:</strong> {formatPrice(commissionModal.preview.baseTotal)}</div>
                    <div><strong>Objectif:</strong> {formatPrice(commissionModal.preview.objective)}</div>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md={12}>
                    <Table bordered size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Taux base</th>
                          <th>Avec paliers</th>
                          <th>Commission base</th>
                          <th>Commission bonus</th>
                          <th>Total commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{(commissionModal.preview.baseRate * 100).toFixed(2)} %</td>
                          <td>{commissionModal.preview.tiersApplied ? 'Oui' : 'Non'}</td>
                          <td>{formatPrice(commissionModal.preview.baseCommission)}</td>
                          <td>{formatPrice(commissionModal.preview.bonusCommission)}</td>
                          <td><strong>{formatPrice(commissionModal.preview.totalCommission)}</strong></td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeCommissionModal}>{t('common.close') || 'Fermer'}</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={planModal.visible} toggle={() => setPlanModal(p => ({ ...p, visible: false }))} size="lg">
        <ModalHeader toggle={() => setPlanModal(p => ({ ...p, visible: false }))}>
          Nouveau plan de commission
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md={4}>
              <FormGroup>
                <Label>Période</Label>
                <Input
                  type="select"
                  value={planModal.data.period}
                  onChange={(e) => setPlanModal(p => ({ ...p, data: { ...p.data, period: e.target.value } }))}
                >
                  <option value="DAYS_15">15 jours</option>
                  <option value="DAYS_30">30 jours</option>
                  <option value="DAYS_45">45 jours</option>
                  <option value="DAYS_60">60 jours</option>
                  <option value="DAYS_90">90 jours</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Base</Label>
                <Input
                  type="select"
                  value={planModal.data.baseType}
                  onChange={(e) => setPlanModal(p => ({ ...p, data: { ...p.data, baseType: e.target.value } }))}
                >
                  <option value="ON_REVENUE">Chiffre d'affaires (HT)</option>
                  <option value="MARGIN">Marge (HT)</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Objectif (HT)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={planModal.data.objectiveAmount}
                  onChange={(e) => setPlanModal(p => ({ ...p, data: { ...p.data, objectiveAmount: Number(e.target.value) } }))}
                  onFocus={(e) => e.target.select()}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <FormGroup>
                <Label>Taux base (%)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={planModal.data.baseRate * 100}
                  onChange={(e) => setPlanModal(p => ({ ...p, data: { ...p.data, baseRate: Number(e.target.value) / 100 } }))}
                  onFocus={(e) => e.target.select()}
                />
                <small className="text-muted">Ex: 5 = 5%</small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Taux bonus (%)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={planModal.data.bonusRate * 100}
                  onChange={(e) => setPlanModal(p => ({ ...p, data: { ...p.data, bonusRate: Number(e.target.value) / 100 } }))}
                  onFocus={(e) => e.target.select()}
                />
                <small className="text-muted">Appliqué uniquement au-dessus de l’objectif</small>
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col md={12}>
              <FormGroup check>
                <Input
                  type="checkbox"
                  checked={planModal.data.progressiveTiersEnabled}
                  onChange={(e) => setPlanModal(p => ({ ...p, data: { ...p.data, progressiveTiersEnabled: e.target.checked } }))}
                />{" "}
                <Label check>Activer les paliers progressifs</Label>
              </FormGroup>
              <small className="text-muted">Pour un “quick create”, tu peux laisser sans paliers (ajoutables plus tard).</small>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              const payload = { ...planModal.data, tiers: planModal.data.progressiveTiersEnabled ? planModal.data.tiers : [] };
              dispatch(commissionPlanActions.create({
                data: payload,
                onSuccess: (created) => {
                  // auto-sélectionner le plan nouvellement créé
                  formik.setFieldValue("commissionPlanId", created.id);
                  setPlanModal(p => ({ ...p, visible: false }));
                },
                onError: (msg) => {
                  // à toi d’ajouter Swal si tu veux ici
                  console.error(msg);
                }
              }));
            }}
          >
            Créer
          </Button>
          <Button color="secondary" onClick={() => setPlanModal(p => ({ ...p, visible: false }))}>
            Annuler
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={ledgerModal.visible} toggle={closeLedger} size="xl">
        <ModalHeader toggle={closeLedger}>
          Historique commissions — {ledgerModal.commercial?.fullName}
        </ModalHeader>
        <ModalBody>
          {ledgerLoading ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : (
            <Table bordered responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Status</th>
                  <th>Période</th>
                  <th>Plan</th>
                  <th>Base</th>
                  <th>Base Comm.</th>
                  <th>Bonus</th>
                  <th>Total</th>
                  <th>Posté le</th>
                  <th>Ref</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(ledgerByCommercialId[ledgerModal.commercial?.id] || []).map((r, idx) => {
                  const isPosted = r.status === "POSTED";
                  const planBadge = r.period
                    ? `${r.period} • ${r.baseType === "ON_REVENUE" ? "CA" : "Marge"}`
                    : "-";
                  return (
                    <tr key={r.id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <Badge color={isPosted ? "success" : "secondary"}>{r.status}</Badge>
                      </td>
                      <td>{r.periodStart} → {r.periodEnd}</td>
                      <td><span className="badge bg-dark">{planBadge}</span></td>
                      <td>{formatPrice(r.baseTotal)}</td>
                      <td>{formatPrice(r.baseCommission)}</td>
                      <td>{formatPrice(r.bonusCommission)}</td>
                      <td><strong>{formatPrice(r.totalCommission)}</strong></td>
                      <td>{r.postedAt?.replace("T", " ").slice(0, 16)}</td>
                      <td>{r.postingRef || "-"}</td>
                      <td>
                        {isPosted ? (
                          <Button
                            size="sm"
                            color="warning"
                            onClick={() => {
                              Swal.fire({
                                icon: "warning",
                                title: "Annuler (reverse) cette écriture ?",
                                text: "Une écriture REVERSED sera ajoutée pour neutraliser ce poste.",
                                showCancelButton: true,
                                confirmButtonText: "Oui",
                                cancelButtonText: "Non",
                              }).then(res => {
                                if (res.isConfirmed) {
                                  // Use the period window from the row
                                  // You need PeriodType + refDate to call reverse API:
                                  // refDate can be r.periodEnd (it reconstructs same window server-side)
                                  const refDate = r.periodEnd;
                                  const period = r?.period; // DAYS_15/30/45/60/90
                                  dispatch(commercialActions.reverseCommissionLedger({
                                    commercialId: ledgerModal.commercial.id,
                                    period,
                                    refDate,
                                    ref: "ui-reverse",
                                    onSuccess: () => Swal.fire({ icon: "success", title: "Réversée" }),
                                    onError: (e) => Swal.fire({ icon: "error", title: "Erreur", text: e?.message })
                                  }));
                                }
                              });
                            }}
                          >
                            Reverse
                          </Button>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(!ledgerByCommercialId[ledgerModal.commercial?.id] ||
                  ledgerByCommercialId[ledgerModal.commercial?.id].length === 0) && (
                    <tr>
                      <td colSpan={11} className="text-center text-muted">Aucune écriture</td>
                    </tr>
                  )}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeLedger}>Fermer</Button>
        </ModalFooter>
      </Modal>


    </Container>
  );
};

export default CommercialList;
